import type { VideoJsPlayer } from 'video.js';
import movieApi from 'src/apis/movie';
import tagMovieApi from 'src/apis/tagMovie';
import mtxApi from 'src/apis/mtx';
import { downloadBlob, downloadUrl } from './downloadHelper';
import type { CameraPosition, Movie, MovieTag } from 'src/models/movie';
import type {
  GIKp,
  GIMovie,
  GIMovieGeoIndex,
  GIMovieList,
} from 'src/models/geoItem';
import type { SensorData } from 'src/models/mtx';
import { getBearing, getDistanceInMeters } from './geoCalcHelper';
import type { RoadNameDisp } from 'src/models/master';
import { logTagMovieAdd, logTagMovieDelete } from './analyticsHelper';
import { dtFormat, ensureDate } from './dateHelper';
import type { Location, MovieTagModalParams } from 'src/models';
import type EMPinLayerManager from './extreme_map/EMPinLayerManager';
import { initGeoItemsMeta } from 'src/models/geoItem';
import type {
  AnalyticsParams,
  BasicPlayerStyleState,
  MoviePlayerState,
  SeekParams,
  VideoControlProperties,
} from 'src/models/moviePlayer';

export function getMgiDistance(mgi1: GIMovieGeoIndex, mgi2: GIMovieGeoIndex): number {
  const c1 = {
    lon: parseFloat(mgi1.location.lon),
    lat: parseFloat(mgi1.location.lat),
  };
  const c2 = {
    lon: parseFloat(mgi2.location.lon),
    lat: parseFloat(mgi2.location.lat),
  };
  return getDistanceInMeters(c1, c2);
}

export async function getScreenshotBlobOfVid(movie: GIMovie, movieDt: Date, vid: string, vjs: VideoJsPlayer): Promise<Blob | string | null> {
  if (!movie || !movieDt || !vid) return null;

  const videoEl = document.querySelector(`#${vid}`)?.querySelector('video');
  const canvas = document.createElement('canvas');
  canvas.width = vjs.videoWidth();
  canvas.height = vjs.videoHeight();
  const ctx = canvas.getContext('2d');
  if (!ctx || !videoEl) return null;

  ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

  const mimeTypeJpeg = 'image/jpeg';
  const jpegQuality = 0.92;
  return new Promise(resolve => {
    if (canvas.toBlob) {
      canvas.toBlob(blob => {
        resolve(blob);
      }, mimeTypeJpeg, jpegQuality);
    } else {
      // toDataURLで戻ってくるのはstringだが、中で呼び出してるFileSaverで
      // stringの場合はCORSのチェックをするためにsynchronousなXMLHttpRequest
      // を投げていて、少なくともchromeだとdeprecatedなwarningが出て気になる
      // のでなるべく使いたくない.
      const blob = canvas.toDataURL(mimeTypeJpeg, jpegQuality);
      resolve(blob);
    }
  });
}

function getCameraPositionForVid(vid: string): CameraPosition | '' {
  if (vid.startsWith('vid1-')) {
    return 'left';
  } else if (vid.startsWith('vid2-')) {
    return 'front';
  } else if (vid.startsWith('vid3-')) {
    return 'right';
  }
  return '';
}

export async function takeScreenshotOfVidWithCameraPosition(
  movie: GIMovie,
  movieDt: Date,
  vid: string,
  vjs: VideoJsPlayer,
  cameraPosition: string,
): Promise<void> {
  const blob = await getScreenshotBlobOfVid(movie, movieDt, vid, vjs);
  if (!blob) return;

  const filename = dtFormat(movieDt, 'yyyymmdd_HHMMSS') +
    '_' + movie.id + '_' + cameraPosition + '.jpg';
  downloadBlob(blob, filename);
}

export async function takeScreenshotOfVid(movie: GIMovie, movieDt: Date, vid: string, vjs: VideoJsPlayer): Promise<void> {
  const position = getCameraPositionForVid(vid);
  await takeScreenshotOfVidWithCameraPosition(movie, movieDt, vid, vjs, position);
}

export interface MovieFileUrlObj {
  left: string;
  right: string;
  front: string;
}

export function initMovieFileUrlObjState(): MovieFileUrlObj {
  return { left: '', right: '', front: '' };
}

// BasicMoviePlayerのMoviePlayerState初期値。
export function initMoviePlayerState(): MoviePlayerState {
  const initStyleState = (): BasicPlayerStyleState => {
    return {
      videoHeight: '48%',
      infoHeight: '51%',
    };
  };
  const initMovieTagModalState = (): MovieTagModalParams => {
    return {
      show: false,
      tags: [],
      movie: null,
    };
  };
  const initVideoControlPropertiesState = (): VideoControlProperties => {
    return {
      playSpeed: 1.0,
      volume: 0,
      // playingだけでは、falseのままでは再実行されないため、
      // 元の動きと異なりますので、playとpause両方でコントロールする。
      play: 0,
      pause: 0,
      tickMsec: 0,
      reset: 0,
      loadMovie: 0,
      isPlayBackward: false,
    };
  };
  const initSeekState = (): SeekParams => {
    return {
      current: 0,
      min: 0,
      max: 100,
      step: 1,
    };
  };
  const initAnalyticsState = (): AnalyticsParams => {
    return {
      accumulatedPlayMsecs: 0,
      tmpCalcBase: -1,
    };
  };
  return {
    isInitialized: false,
    movieList: {
      listSelection: {
        currentMovieLists: [],
        currentMovieListId: '',
        currentMovieList: null,
        showMovieListSelect: false,
      },
      current: {
        currentMovie: null,
        currentMovieIdx: -1,
        movieFileUrlObj: initMovieFileUrlObjState(),
        isFirstMovie: false,
        isLastMovie: false,
        isCurrentMovieReady: false,
        continuedMovies: [],
        isMovieEnded: false,
        isTryingToPlayAnotherMovie: false,
      },
      timeDisp: {
        startTimeDisp: '00:00:00',
        endTimeDisp: '00:00:00',
      },
    },
    geo: {
      currentMgi: null,
      currentMgiIdx: -1,
      locationDisp: '',
      currentMovieCurrentDt: null,
      geoItemsMeta: initGeoItemsMeta(),
      mapElemInfo: null,
      createMapElemFailed: 0,
    },
    playback: {
      videoControlProperties: initVideoControlPropertiesState(),
      currentTime: 0,
      titleBarTexts: { dateDisp: '', locationDisp: '' },
      vjsLoadedData: {},
      vjsPrepared: {},
      vjsError: [],
      seek: initSeekState(),
      isSeeking: false,
      isPlaying: false,
    },
    ui: {
      isDownloadingMovieFile: false,
      isDownloadingScreenshot: false,
      showSensorChart: false,
      sensorData: [],
      movieTagModal: initMovieTagModalState(),
      styles: initStyleState(),
      analytics: initAnalyticsState(),
    },
  };
}

export async function getMovieFileUrlObj(movieId: number): Promise<MovieFileUrlObj> {
  const movieFiles = await movieApi.getMovieFiles(movieId);
  const ret = { front: '', left: '', right: '' };
  for (const fileInfo of Object.values(movieFiles.movieFileMap)) {
    // MovieFileMapはカメラ位置ごとにキーが欠けうる型なので、値の無いカメラ位置は飛ばす
    if (!fileInfo) continue;

    // CloudFront Signed URLをそのまま使用
    const moviePath = fileInfo.signedUrl;
    if (fileInfo.cameraPosition === 'front') {
      ret.front = moviePath;
    } else if (fileInfo.cameraPosition === 'left') {
      ret.left = moviePath;
    } else if (fileInfo.cameraPosition === 'right') {
      ret.right = moviePath;
    }
  }
  return ret;
}

export async function downloadMovieFile(movie: GIMovie, position: CameraPosition): Promise<void> {
  if (!movie) return;

  // 一応取り直す
  const urlObj = await getMovieFileUrlObj(movie.id);
  const url = urlObj[position];
  // その動画に存在しないカメラ位置はURLが空になる。空URLで取得すると
  // 現在のページのHTMLがmp4として保存されてしまうため、ここで打ち切る
  if (!url) return;

  const filename = dtFormat(movie.ts, 'yyyymmdd_HHMMSS') +
    '_' + movie.id + '_' + position + '.mp4';
  await downloadUrl(url, filename);
}

export async function downloadMovieFileOfVid(movie: GIMovie, vid: string): Promise<void> {
  if (!movie || !vid) return;

  const position = getCameraPositionForVid(vid);
  if (!position) return;

  await downloadMovieFile(movie, position);
}

export function convMovies(movies: Movie[], kpMapByKpUid: Map<string, GIKp>): GIMovie[] {
  return movies.map<GIMovie>(movie => (
    {
      ...movie,
      movieGeoIndices: movie.movieGeoIndices.map<GIMovieGeoIndex>(movieGeoIndex => {
        const kilopost = movieGeoIndex.kpUid ? kpMapByKpUid.get(movieGeoIndex.kpUid) : null;
        return {
          ...movieGeoIndex,
          kilopost: kilopost ? { ...kilopost } : null,
        };
      }),
    }
  ));
}

export async function searchContinuedMovies(currentMovie: GIMovie, kpMapByKpUid: Map<string, GIKp>): Promise<GIMovie[]> {
  const params = {
    device_id: currentMovie.deviceId,
    start_ts: currentMovie.ts ?? new Date(currentMovie.startTs),
    quantity: 5,
  };
  const movies = await movieApi.searchContinuedMovies(params);
  return convMovies(movies, kpMapByKpUid);
}

function getLatLonDisp_(lat: number, lon: number): string {
  return `(緯度: ${convertDecimalLatLonToDms_(lat)}, 経度: ${convertDecimalLatLonToDms_(lon)})`;
}

function convertDecimalLatLonToDms_(latLon: number): string {
  const deg = Math.floor(latLon);
  const min = Math.floor((latLon - deg) * 60);
  const sec = ((latLon - deg) * 60 - min) * 60;
  return `${deg}°${min}′${sec.toFixed(4)}″`;
}

export function preCalculateMovieVals(movieList: GIMovieList, roadNameDispMap: Record<string, RoadNameDisp>): void {
  if (movieList.isPreCalculated) return;

  // 再生に必要な数値類を事前に計算しておく
  // playXXX: サーバ側からもらった、動画ごとの再生範囲
  // hardXXX: 画面側で決定した、再生しようと思えば再生できる範囲
  let accumMsec = 0;
  movieList.movies.forEach((movie, idx) => {
    movie.durationMsec = parseFloat(movie.duration) * 1000;
    // movieの中で検索にあたった範囲についての再生情報
    movie.playStartOffsetMsec = movie.startOffsetMsec || 0;
    movie.playEndOffsetMsec = movie.endOffsetMsec || movie.durationMsec;
    movie.playDurationMsec = movie.playEndOffsetMsec - movie.playStartOffsetMsec;

    // 実際にプレイヤーで再生できる範囲についての再生情報
    movie.hardStartOffsetMsec = movie.playStartOffsetMsec;
    movie.hardEndOffsetMsec = movie.playEndOffsetMsec;
    // ルート検索でない場合、最初と最後の動画については
    // 検索範囲外であっても再生したければさせる
    if (movieList.searchType !== 'route') {
      if (idx === 0) {
        movie.hardStartOffsetMsec = 0;
      }
      if (idx === movieList.movies.length - 1) {
        movie.hardEndOffsetMsec = movie.durationMsec;
      }
    }

    // この動画の直前までの経過再生時間msec
    movie.accumMsec = accumMsec;
    accumMsec += movie.playDurationMsec;

    movie.ts = ensureDate(movie.startTs);

    const mgis = movie.movieGeoIndices;
    // 元のmgisで計算するために最後に追加した次の動画のmgiを除く
    const mgisLength = mgis.filter(e => e.movieId === movie.id).length;
    for (let i = 0; i < mgisLength; i++) {
      const mgi = mgis[i];
      if (!movie.ts) { continue; }
      mgi.ts = new Date(movie.ts.valueOf() + mgi.tsMsecDiff);
      const nextTsMsecDiff = i < mgisLength - 1
        ? mgis[i + 1].tsMsecDiff : movie.playDurationMsec;
      mgi.startMsecDiff = mgi.tsMsecDiff;
      mgi.endMsecDiff = nextTsMsecDiff;

      mgi.locationDisp = '';
      if (mgi.kilopost) {
        const roadNameDisp = mgi.kilopost.roadNameDisp;
        mgi.kilopost.roadNameDispShort = roadNameDispMap[roadNameDisp]?.roadNameDispShort;
        // vjs-custom-title-barに表示されるlocation
        mgi.locationDisp = [
          mgi.kilopost.roadNameDispShort,
          mgi.kilopost.direction,
          mgi.kilopost.placeName === 'main_line' ? '' : mgi.kilopost.placeName,
          parseFloat(mgi.kilopost.kp.toString()).toFixed(1) + 'km',
          '付近',
          getLatLonDisp_(parseFloat(mgi.location.lat), parseFloat(mgi.location.lon)),
        ].filter(e => !!e).join(' ');
      }
    }
  });

  if (movieList.searchType !== 'route') {
    // ルート検索以外の場合、hard start/end は動画で再生可能な範囲の全体
    const firstMovie = movieList.movies[0];
    const lastMovie = movieList.movies[movieList.movies.length - 1];
    if (!firstMovie.ts || !lastMovie.ts) return;

    movieList.hardStartTs =
      new Date(firstMovie.ts.valueOf() + (firstMovie.hardStartOffsetMsec || 0));
    movieList.hardEndTs =
      new Date(lastMovie.ts.valueOf() + (lastMovie.hardEndOffsetMsec || 0));
  } else {
    // ルート検索の場合、hard start/end は与えられたstart_tsとend_tsそのもの
    movieList.hardStartTs = new Date(movieList.startTs?.toString() || '');
    movieList.hardEndTs = new Date(movieList.endTs?.toString() || '');
  }
  movieList.fullDurationMsec = movieList.hardEndTs.getTime() - movieList.hardStartTs.getTime();

  movieList.mightCalculateClosestMgi = true;
  movieList.isPreCalculated = true;
}

export async function getSensorDataOfMovieList(ml: GIMovieList): Promise<SensorData[]> {
  const movieIds = ml.movies.map(e => e.id);
  return await mtxApi.fetchByMovieIds({ movie_ids: movieIds });
}

export async function updateMovieTags(resultTags: MovieTag[], targetMovie: GIMovie): Promise<void> {
  const tagIds = resultTags.map(e => e.id);

  let adds = 0;
  let deletes = targetMovie.tags.length;
  const currentTagMap = targetMovie.tags.reduce((acc: Record<number, MovieTag>, e) => {
    acc[e.id] = e; return acc;
  }, {});
  for (const resTag of resultTags) {
    if (!currentTagMap[resTag.id]) {
      // 今自分についてなければ新たに追加したことになる
      ++adds;
    } else {
      // 自分についてるやつは、削除件数から引き算
      --deletes;
    }
  }

  await tagMovieApi.update(targetMovie.id, { tag_ids: tagIds });
  targetMovie.tags = resultTags;

  if (adds > 0) { logTagMovieAdd(1); }
  if (deletes > 0) { logTagMovieDelete(1); }
}

interface OpenNewInfraDoctorTabParamsInner {
  lat: string;
  lon: string;
  altitude?: number;
  heading: number;
  pitch?: number;
  roll?: number;
}

function openNewInfraDoctorTab_(
  ifdLinkPrefix: string,
  { lat, lon, altitude = 0, heading, pitch = 0, roll = 0 }: OpenNewInfraDoctorTabParamsInner,
) {
  const params = [];
  params.push(`lat=${lat}`);
  params.push(`lon=${lon}`);
  params.push(`altitude=${altitude}`);
  params.push(`heading=${heading}`);
  params.push(`pitch=${pitch}`);
  params.push(`roll=${roll}`);
  window.open(`${ifdLinkPrefix}&${params.join('&')}`, '_blank');
}

interface OpenNewInfraDoctorTabParams {
  ifdLinkPrefix: string;
  currentMovieList: GIMovieList;
  currentMovie: GIMovie;
  currentMovieIdx: number;
  currentMgi: GIMovieGeoIndex;
  currentMgiIdx: number;
  isFirstMovie: boolean;
  isLastMovie: boolean;
}

export function openNewInfraDoctorTab(params: OpenNewInfraDoctorTabParams): void {
  const {
    ifdLinkPrefix,
    currentMovieList,
    currentMovie,
    currentMovieIdx,
    currentMgi,
    currentMgiIdx,
    isFirstMovie,
    isLastMovie,
  } = params;

  // 次のmgi、なければ前のmgiを取得する
  let pt1: GIMovieGeoIndex | null = null;
  let pt2: GIMovieGeoIndex | null = null;
  const mgis = currentMovie.movieGeoIndices;
  if (currentMgiIdx < mgis.length - 1) {
    // 同一movie内で次のmgiを発見
    pt1 = currentMgi;
    pt2 = mgis[currentMgiIdx + 1];
  } else if (!isLastMovie) {
    // 次の動画の最初のmgiが次のmgi
    const nextMovieIdx = currentMovieIdx + 1;
    const nextMovie = currentMovieList.movies[nextMovieIdx];
    pt1 = currentMgi;
    pt2 = nextMovie.movieGeoIndices[0];
  } else if (currentMgiIdx > 0) {
    // 同一movie内で前のmgiを発見
    pt1 = mgis[currentMgiIdx - 1];
    pt2 = currentMgi;
  } else if (!isFirstMovie) {
    // 前の動画の最後のmgiが前のmgi
    const prevMovieIdx = currentMovieIdx - 1;
    const prevMovie = currentMovieList.movies[prevMovieIdx];
    const lastIdx = prevMovie.movieGeoIndices.length - 1;
    pt1 = prevMovie.movieGeoIndices[lastIdx];
    pt2 = currentMgi;
  }
  if (!pt1 || !pt2) return;

  // -180 <= x <= 180 (真北が0度で時計側はプラス、反時計側はマイナス)から
  // 0 <= x <= 360 (真北が0度で時計回りに増加)へ
  const bearing = getBearing(
    {lat: parseFloat(pt1.location.lat), lon: parseFloat(pt1.location.lon)},
    {lat: parseFloat(pt2.location.lat), lon: parseFloat(pt2.location.lon)});
  const ifdHeading = bearing < 0 ? bearing + 360 : bearing;

  openNewInfraDoctorTab_(ifdLinkPrefix, {
    lat: currentMgi.location.lat,
    lon: currentMgi.location.lon,
    heading: ifdHeading,
  });
}

function getLastMgiBeforeKilopostChange(
  mgis: GIMovieGeoIndex[],
  kpUid: string,
  currentMgiIndex: number,
): GIMovieGeoIndex {
  for (let i = currentMgiIndex; i >= 0; i--) {
    const mgi = mgis[i];
    if (mgi.kpUid !== kpUid) return mgis[i + 1];

  }
  return mgis[0];
}

function getNextMgiAfterKilopostChange(
  mgis: GIMovieGeoIndex[],
  kpUid: string,
  currentMgiIndex: number,
): GIMovieGeoIndex | null {
  for (let i = currentMgiIndex + 1; i < mgis.length; i++) {
    const mgi = mgis[i];
    if (mgi.kpUid !== kpUid) return mgi;

  }
  return null;
}

interface GetCurrentLocationDisplayInfoParams {
  currentTimestamp: Date;
  movieGeoIndices: GIMovieGeoIndex[];
  currentMgi: GIMovieGeoIndex;
  currentMgiIndex: number;
}
interface LocationDisplayInfo {
  movieMetaInfoLocationDisp: string;
  location: Location;
}
export function getCurrentLocationDisplayInfo(params: GetCurrentLocationDisplayInfoParams): LocationDisplayInfo {
  if (!params.currentMgi.kilopost) {
    return {
      movieMetaInfoLocationDisp: '',
      location: { lat: parseFloat(params.currentMgi.location.lat), lon: parseFloat(params.currentMgi.location.lon) },
    };
  }
  const adjustedKpInfo = getAdjustedKilopostInfo({
    currentTimestamp: params.currentTimestamp,
    movieGeoIndices: params.movieGeoIndices,
    currentKilopost: params.currentMgi.kilopost,
    currentMgiIndex: params.currentMgiIndex,
  });
  if (!adjustedKpInfo) {
    return {
      movieMetaInfoLocationDisp: formatToLocationDisplay(
        params.currentMgi.kilopost,
        params.currentMgi.kilopost.kp,
        getLatLonDisp_(parseFloat(params.currentMgi.location.lat), parseFloat(params.currentMgi.location.lon)),
      ),
      location: { lat: parseFloat(params.currentMgi.location.lat), lon: parseFloat(params.currentMgi.location.lon) },
    };
  }
  return {
    movieMetaInfoLocationDisp: formatToLocationDisplay(
      params.currentMgi.kilopost,
      adjustedKpInfo.adjustedKpCalc,
      getLatLonDisp_(adjustedKpInfo.adjustedLocation.lat, adjustedKpInfo.adjustedLocation.lon),
    ),
    location: adjustedKpInfo.adjustedLocation,
  };
}

interface GetAdjustedKilopostInfoParams {
  currentTimestamp: Date;
  movieGeoIndices: GIMovieGeoIndex[];
  currentKilopost: GIKp;
  currentMgiIndex: number;
}
interface GetAdjustedKilopostInfo {
  adjustedLocation: Location;
  adjustedKpCalc: number;
}
function getAdjustedKilopostInfo(params: GetAdjustedKilopostInfoParams): GetAdjustedKilopostInfo | null {
  const lastMgiBeforeKilopostChange = getLastMgiBeforeKilopostChange(
    params.movieGeoIndices,
    params.currentKilopost.kpUid,
    params.currentMgiIndex,
  );
  if (!lastMgiBeforeKilopostChange.ts) return null;

  const nextMgiAfterKilopostChange = getNextMgiAfterKilopostChange(
    params.movieGeoIndices,
    params.currentKilopost.kpUid,
    params.currentMgiIndex,
  );
  const nextKp = nextMgiAfterKilopostChange?.kilopost;
  if (!nextMgiAfterKilopostChange || !nextMgiAfterKilopostChange.ts || !nextKp) return null;

  const startLocationInfo = {
    ts: lastMgiBeforeKilopostChange.ts,
    lat: parseFloat(lastMgiBeforeKilopostChange.location.lat),
    lon: parseFloat(lastMgiBeforeKilopostChange.location.lon),
  };
  const endLocationInfo = {
    ts: nextMgiAfterKilopostChange.ts,
    lat: parseFloat(nextMgiAfterKilopostChange.location.lat),
    lon: parseFloat(nextMgiAfterKilopostChange.location.lon),
  };
  const { adjustedLocation, adjustedDistanceKilometers } = calculateAdjustedLocationAndDistance(
    params.currentTimestamp,
    startLocationInfo,
    endLocationInfo,
  );
  const adjustedKpCalc = calculateAdjustedKp({
    currentKp: params.currentKilopost,
    nextKp,
    adjustedDistanceKilometers,
  });
  return {
    adjustedLocation,
    adjustedKpCalc,
  };
}

interface LocationInfo {
  ts: Date;
  lat: number;
  lon: number;
}
interface AdjustedLocationAndDistance {
  adjustedLocation: Location;
  adjustedDistanceKilometers: number;
}
function calculateAdjustedLocationAndDistance(
  currentTimestamp: Date,
  startLocationInfo: LocationInfo,
  endLocationInfo: LocationInfo,
): AdjustedLocationAndDistance {
  // 現在のkilopostと次のkilopostに到達した時の地点情報と現在の時刻情報から
  // 現在のkilopostの位置からどれくらい進んだか計算し補正した地点情報を取得する
  if (!startLocationInfo.ts || !endLocationInfo.ts) {
    return {
      adjustedLocation: { lat: startLocationInfo.lat, lon: startLocationInfo.lon },
      adjustedDistanceKilometers: 0,
    };
  }
  const currentTimestampMsec = currentTimestamp.getTime();
  const currentKpMgiTsMsec = startLocationInfo.ts.getTime();
  const nextKpMgiTsMsec = endLocationInfo.ts.getTime();
  const tsDiff = (currentTimestampMsec - currentKpMgiTsMsec) / (nextKpMgiTsMsec - currentKpMgiTsMsec);
  const adjustedLat = (endLocationInfo.lat - startLocationInfo.lat) * tsDiff + startLocationInfo.lat;
  const adjustedLon = (endLocationInfo.lon - startLocationInfo.lon) * tsDiff + startLocationInfo.lon;
  const currentKpLocation = { lat: startLocationInfo.lat, lon: startLocationInfo.lon };
  const adjustedLocation = { lat: adjustedLat, lon: adjustedLon };
  const adjustedDistance = getDistanceInMeters(currentKpLocation, adjustedLocation);
  // 10m単位で四捨五入
  const adjustedDistanceKilometers = Math.round(adjustedDistance / 10) * 10 / 1000;
  return {
    adjustedLocation: { lat: adjustedLat, lon: adjustedLon },
    adjustedDistanceKilometers,
  };
}

function formatToLocationDisplay(kilopost: GIKp, kp: number, latLonDisp: string): string {
  return [
    kilopost.roadNameDispShort,
    kilopost.direction,
    kilopost.placeName === 'main_line' ? '' : kilopost.placeName,
    kp.toFixed(1) + 'km',
    '付近',
    latLonDisp,
  ].filter(e => !!e).join(' ');
}

interface CalculateAdjustedKpParams {
  currentKp: GIKp;
  nextKp: GIKp;
  adjustedDistanceKilometers: number;
}
function calculateAdjustedKp(params: CalculateAdjustedKpParams): number {
  const directionSign = params.currentKp.kpCalc > params.nextKp.kpCalc ? -1 : 1;
  const rawKpCalc = params.currentKp.kpCalc + directionSign * params.adjustedDistanceKilometers;
  const adjustedKpCalc = Math.round(rawKpCalc * 10) / 10;
  // 補正したkpCalcがnextKpのkp_calcより先に進んでいたらnextKpのkp_calcを表示する
  return directionSign * (params.nextKp.kpCalc - adjustedKpCalc) < 0 ? params.nextKp.kpCalc : adjustedKpCalc;
}

export function isTimeDispChanged(newTimestamp: Date | null, oldTimestamp: Date | null): boolean {
  if (!newTimestamp || !oldTimestamp) return false;

  // setMilliseconds() は破壊的変更のため、算術でミリ秒を切り捨てて比較する
  // 逆再生時を考慮し絶対値で比較する
  return Math.abs(Math.trunc(newTimestamp.getTime() / 1000) - Math.trunc(oldTimestamp.getTime() / 1000)) >= 1;
}

function complementMgiTs(mgi: GIMovieGeoIndex, movieStartTs: string): void {
  const movieTs = ensureDate(movieStartTs);
  if (!movieTs || mgi.ts) return;

  mgi.ts = new Date(movieTs.valueOf() + mgi.tsMsecDiff);
}

export function appendFirstMgisFromContinuedMovieToLastMovie(
  movieList: GIMovieList | null,
  origContinuedMovies: GIMovie[],
): void {
  if (!movieList) return;

  const continuedMovies = origContinuedMovies.filter(e => {
    return movieList.movies.every(movie => movie.id !== e.id);
  });
  const firstContinuedMovie = continuedMovies[0];
  const firstContinuedMovieMgi = firstContinuedMovie?.movieGeoIndices[0];
  if (!firstContinuedMovieMgi) return;

  const lastMovie = movieList.movies[movieList.movies.length - 1];
  if (lastMovie.movieGeoIndices.some(e => e.id === firstContinuedMovieMgi.id)) return;

  // 位置の補正をするためにmgiのtsを利用しているためtsを補完する
  complementMgiTs(firstContinuedMovieMgi, firstContinuedMovie.startTs);
  lastMovie.movieGeoIndices.push(firstContinuedMovieMgi);
}

// movieList.startOffset が指定されている場合の分岐は呼び出し側（単一ストリーム系のみ有効な仕様）で行う。
// 比較モードにはこの分岐がないため、このループ部分のみを共有する。
export function findFirstSeekPointFromHitMgiMap(movieList: GIMovieList): number {
  let targetMgiId = null;
  const minDistance = movieList.minDistance;
  // 範囲検索以外の場合は99999になっているはず
  if (minDistance > 1000) return 0;

  for (const [mgiId, distance] of Object.entries(movieList.hitMgiMap)) {
    if (Math.abs(minDistance - parseInt(distance.toString())) < Number.EPSILON) {
      targetMgiId = mgiId.toString();
      break;
    }
  }
  if (!targetMgiId) return 0;

  for (const movie of movieList.movies) {
    for (const mgi of movie.movieGeoIndices) {
      if (mgi.id.toString() === targetMgiId) return (movie.accumMsec || 0) + (mgi.startMsecDiff || 0);

    }
  }
  return 0;
}

export interface NextMovieGeoIndex {
  mgi: GIMovieGeoIndex;
  idx: number;
}

export function getNextMovieGeoIndex(
  currentMovieId: number,
  movieGeoIndices: GIMovieGeoIndex[],
  currentMgiIdx: number,
): NextMovieGeoIndex | null {
  const mgis = movieGeoIndices.filter(e => e.movieId === currentMovieId);
  if (currentMgiIdx >= mgis.length - 1) return null;

  return { mgi: mgis[currentMgiIdx + 1], idx: currentMgiIdx + 1 };
}

export function applyMapPinPosition(
  layerManager: EMPinLayerManager,
  pinId: string,
  lat: string,
  lon: string,
): void {
  const point = { lat: parseFloat(lat), lon: parseFloat(lon) };
  layerManager.updatePinPosition(pinId, point);
}
